import type {
    ChartAccount,
    OperationAmountSource,
    OperationLineDraft,
    OperationLineSide,
    OperationPairRole,
    OperationTemplateDetail,
    OperationVersionDetail,
    UserRole,
} from '@/api/models';
import type { ErrorResponse } from '@/api/handlers/responses';

/**
 * The editor's pure state helpers and presentational config.
 *
 * Everything here is a function of its arguments: the hook holds the draft in
 * `useState` and moves it by calling these, the View renders what comes out.
 * That is what keeps the pair arithmetic — which is fiddly, and which the server
 * validates independently — testable without rendering anything.
 */

/** The sample amount the preview opens with: ₾ 118.00, one VAT-inclusive hundred. */
export const DEFAULT_SAMPLE_GEL = '118.00';

/** A pair, as the editor works in it: always a debit and a credit, in that order. */
export type DraftPair = {
    pairGroup: number;
    /**
     * What the pair does, from the closed taxonomy.
     *
     * Held once here and written to **both** lines on the way out. The wire has
     * no pair object — a pair is lines sharing a `pairGroup` — so the field has
     * to ride on the legs, and holding it twice in the editor would be two
     * places for it to disagree.
     */
    role: OperationPairRole;
    debit: OperationLineDraft;
    credit: OperationLineDraft;
};

const emptyLine = (
    pairGroup: number,
    side: OperationLineSide,
): OperationLineDraft => ({
    pairGroup,
    side,
    accountId: null,
    // Empty strings rather than nulls, because that is what makes this a
    // *slot* under `isSlot` — see `newPair` for why a blank line should start
    // as one. A null pair of labels is the other blank: a fixed line whose
    // account has not been picked yet.
    slotLabelKa: '',
    slotLabelEn: '',
    accountFilter: [],
    amountSource: 'GROSS',
    roleLabel: null,
    pairRole: 'OTHER',
});

/**
 * A brand-new pair: two empty slots on `GROSS`.
 *
 * Slots rather than fixed accounts, because a slot is the safer default to be
 * half-finished in — an unfilled slot is a question the recorder answers,
 * whereas a fixed line the author forgot to set would post everything to
 * whichever account happened to be first in the picker.
 */
export const newPair = (pairGroup: number): DraftPair => ({
    pairGroup,
    // `OTHER` rather than a guess: the taxonomy is required, and seeding a new
    // pair as COGS because that is first in the list would put a wrong label on
    // every pair whose author never opened the picker.
    role: 'OTHER',
    debit: emptyLine(pairGroup, 'DEBIT'),
    credit: emptyLine(pairGroup, 'CREDIT'),
});

/**
 * Group flat lines into pairs.
 *
 * A pair missing a side gets an empty one rather than being dropped: the server
 * would refuse such a recipe (`PAIR_UNBALANCED`), but the editor has to be able
 * to *show* it, or a template that arrived malformed could never be repaired
 * here.
 */
export const toPairs = (lines: readonly OperationLineDraft[]): DraftPair[] => {
    const groups = [...new Set(lines.map((line) => line.pairGroup))].sort(
        (a, b) => a - b,
    );
    return groups.map((pairGroup) => {
        const debit =
            lines.find(
                (l) => l.pairGroup === pairGroup && l.side === 'DEBIT',
            ) ?? emptyLine(pairGroup, 'DEBIT');
        const credit =
            lines.find(
                (l) => l.pairGroup === pairGroup && l.side === 'CREDIT',
            ) ?? emptyLine(pairGroup, 'CREDIT');
        return {
            pairGroup,
            // The debit's copy wins when the two disagree. They cannot disagree
            // through this editor, but a recipe written by an older client or by
            // the seeder can arrive that way, and picking a side beats
            // rendering two roles for one pair.
            role: debit.pairRole,
            debit,
            credit,
        };
    });
};

/** Flatten pairs back to the wire's line list, renumbered dense from 1. */
export const toLines = (pairs: readonly DraftPair[]): OperationLineDraft[] =>
    pairs.flatMap((pair, index) => [
        { ...pair.debit, pairGroup: index + 1, pairRole: pair.role },
        { ...pair.credit, pairGroup: index + 1, pairRole: pair.role },
    ]);

/**
 * The pairs the editor opens with.
 *
 * The draft wins over the version in force: an unpublished draft is where the
 * author left off, and reopening the editor on the live recipe instead would
 * silently discard it. With neither, one empty pair — a blank form, not a blank
 * page.
 */
export const initialPairs = (
    draft: OperationVersionDetail | null,
    active: OperationVersionDetail | null,
): DraftPair[] => {
    const source = draft ?? active;
    if (!source || source.lines.length === 0) {
        return [newPair(1)];
    }
    return toPairs(source.lines);
};

// ── moving a pair ───────────────────────────────────────────────────────────

/**
 * Replace one side of one pair.
 *
 * **The amount source is applied to both sides**, always. The server requires
 * the two to agree (`PAIR_SOURCE_MISMATCH`) because a pair whose sides take
 * different figures cannot balance — so the editor never lets them disagree in
 * the first place, rather than letting the author build something invalid and
 * refusing it on save.
 */
export const setLine = (
    pairs: readonly DraftPair[],
    pairGroup: number,
    side: OperationLineSide,
    patch: Partial<OperationLineDraft>,
): DraftPair[] =>
    pairs.map((pair) => {
        if (pair.pairGroup !== pairGroup) {
            return pair;
        }
        const key = side === 'DEBIT' ? 'debit' : 'credit';
        const other = side === 'DEBIT' ? 'credit' : 'debit';
        const next: DraftPair = { ...pair, [key]: { ...pair[key], ...patch } };
        if (patch.amountSource) {
            next[other] = { ...pair[other], amountSource: patch.amountSource };
        }
        return next;
    });

/**
 * Switch a line between a fixed account and a slot.
 *
 * The half being left is **cleared**, not kept: the server refuses a line that
 * carries both (`LINE_ACCOUNT_AMBIGUOUS`), and holding the old text invisibly so
 * it can be restored on a toggle-back is how a form ends up submitting something
 * the author cannot see.
 */
export const setLineKind = (
    pairs: readonly DraftPair[],
    pairGroup: number,
    side: OperationLineSide,
    kind: 'fixed' | 'slot',
): DraftPair[] =>
    setLine(
        pairs,
        pairGroup,
        side,
        kind === 'fixed'
            ? {
                  accountId: null,
                  slotLabelKa: null,
                  slotLabelEn: null,
                  accountFilter: [],
              }
            : {
                  accountId: null,
                  slotLabelKa: '',
                  slotLabelEn: '',
                  accountFilter: [],
              },
    );

/** Set the pair's role. One control, both legs — see {@link DraftPair.role}. */
export const setPairRole = (
    pairs: readonly DraftPair[],
    pairGroup: number,
    role: OperationPairRole,
): DraftPair[] =>
    pairs.map((pair) =>
        pair.pairGroup === pairGroup ? { ...pair, role } : pair,
    );

/**
 * Which account box is open for editing.
 *
 * **One for the whole recipe, not one per box.** Two boxes open at once would
 * let an author edit a debit while a credit's unsaved keystrokes sat behind it,
 * and the state lives on the page rather than inside `LineBox` for exactly that
 * reason — a box cannot open itself without closing whichever was open.
 */
export type OpenLine = {
    pairGroup: number;
    side: OperationLineSide;
};

/**
 * The pair a drop dialog is asking about, resolved down to what it has to say.
 *
 * Built once when the link is pressed rather than read live off the draft, so
 * the dialog keeps describing the pair the author clicked even though removing
 * it renumbers everything below.
 */
export type PendingDrop = {
    pairGroup: number;
    /** 1-based position as the card is labelled — what the sentence names. */
    displayNumber: number;
    role: OperationPairRole;
    /** `1110 → 6110 · Sales revenue`, so a number becomes a recognisable pair. */
    summary: string;
    /**
     * Whether this pair exists in a version that has already published. It is
     * the difference between "remove" and "drop from v4", and between a plain
     * question and one that has to explain what happens to posted entries.
     */
    isPublished: boolean;
    /** The version in force, named in the published wording. */
    activeVersion: number;
};

/**
 * Whether a line is currently a slot rather than a fixed account.
 *
 * Keyed on the **slot labels**, not on `accountId === null`, and the difference
 * is a state only the editor can be in. On the wire the two discriminators
 * agree — a line carries an account or it carries labels, never both and never
 * neither. But `setLineKind(…, 'fixed')` clears the labels and leaves
 * `accountId` null until the author picks one, and an account-keyed test reads
 * that half-finished line as a slot: press "Fixed", nothing changes, press it
 * again, still nothing. Asking the labels means the toggle answers on the
 * first press and the box shows the account picker it just promised.
 */
export const isSlot = (line: OperationLineDraft): boolean =>
    line.slotLabelKa !== null || line.slotLabelEn !== null;

export const addPair = (pairs: readonly DraftPair[]): DraftPair[] => [
    ...pairs,
    newPair(Math.max(0, ...pairs.map((p) => p.pairGroup)) + 1),
];

/**
 * Remove a pair, keeping at least one.
 *
 * A recipe with no pairs is refused by the server (`NO_PAIRS`) and is not a
 * state the editor should be able to reach — an empty form with an "add pair"
 * button is a worse blank than a form with one blank pair in it.
 */
export const removePair = (
    pairs: readonly DraftPair[],
    pairGroup: number,
): DraftPair[] => {
    if (pairs.length <= 1) {
        return [...pairs];
    }
    return pairs.filter((pair) => pair.pairGroup !== pairGroup);
};

// ── what the header says about this template ────────────────────────────────

/**
 * The version number a publish from here would create.
 *
 * The draft's own number when one exists — publishing promotes *that* draft, it
 * does not open a new one — and otherwise one past the highest version on
 * record. `Math.max` over an empty list is `-Infinity`, hence the `0` seed: a
 * template with no versions at all publishes `v1`.
 */
export const nextVersionNumber = (
    template: OperationTemplateDetail | null,
): number => {
    if (!template) {
        return 1;
    }
    return (
        template.draftVersion?.version ??
        Math.max(0, ...template.versions.map((version) => version.version)) + 1
    );
};

/** The chip beside the navbar title: which version the screen is about. */
export type EditorVersionChip = {
    labelKey: string;
    /** `{{version}}` for the label, absent on the two versionless states. */
    version: number | null;
    tone: 'draft' | 'active' | 'blocked' | 'none';
};

/**
 * What the title chip says.
 *
 * **The draft wins over the version in force**, and that ordering is the whole
 * point of the chip. The editor opens on the draft when there is one, so a
 * screen showing someone's unpublished v4 while its chip read `v3 active` would
 * be describing a recipe that is not on screen.
 *
 * Blocked is reported ahead of the active version for the opposite reason: a
 * blocked template may well still have a version in force, and "active" is the
 * one word that must not appear on an operation nobody can record.
 */
export const versionChip = (
    template: OperationTemplateDetail | null,
): EditorVersionChip => {
    if (template?.draftVersion) {
        return {
            labelKey: 'operations.editor.chip.draft',
            version: template.draftVersion.version,
            tone: 'draft',
        };
    }
    if (template?.status === 'BLOCKED') {
        return {
            labelKey: 'operations.editor.chip.blocked',
            version: null,
            tone: 'blocked',
        };
    }
    if (template?.activeVersion) {
        return {
            labelKey: 'operations.editor.chip.active',
            version: template.activeVersion.version,
            tone: 'active',
        };
    }
    return {
        labelKey: 'operations.editor.chip.none',
        version: null,
        tone: 'none',
    };
};

/**
 * The dot-separated line under the template's displayed name.
 *
 * Two clauses, each dropped rather than faked when its fact is missing: how
 * many pairs are on screen *now*, and which version is in force since when.
 * The name in the other language *used* to be a third clause here, back when
 * the name was read-only text; now that both languages are editable fields
 * in the header itself, repeating one of them as subtitle text would just be
 * the same value shown twice.
 *
 * **The pair count comes from the draft being edited, not from the server's
 * summary.** It is the header's one live figure — add a pair and it moves — and
 * a count that lagged the form by a save would be worse than no count.
 *
 * **"balanced" is only claimed when it is true.** The word is load-bearing: a
 * half-built pair is exactly when the header must stop saying it, which is why
 * this takes the preview's verdict rather than assuming it.
 */
export type EditorSubtitleParts = {
    pairCount: number;
    balanced: boolean;
    activeVersion: number | null;
    activeSince: string | null;
};

export const subtitleParts = (
    template: OperationTemplateDetail | null,
    pairs: readonly DraftPair[],
    balanced: boolean,
): EditorSubtitleParts => ({
    pairCount: pairs.length,
    balanced,
    activeVersion: template?.activeVersion?.version ?? null,
    activeSince: template?.activeVersion?.effectiveFrom ?? null,
});

/**
 * One clause of the subtitle: either a literal already in the right language
 * (the English name is data, not copy) or a key for the View to translate.
 */
export type SubtitleClause =
    | { kind: 'text'; text: string }
    | {
          kind: 'key';
          key: string;
          params: Record<string, string | number | null>;
      };

/** The clauses, in order, with the unknown ones already dropped. */
export const subtitleClauses = (
    parts: EditorSubtitleParts,
): SubtitleClause[] => {
    const clauses: SubtitleClause[] = [];

    clauses.push({
        kind: 'key',
        key: parts.balanced
            ? 'operations.editor.subtitle.pairsBalanced'
            : 'operations.editor.subtitle.pairs',
        params: { count: parts.pairCount },
    });

    clauses.push(
        parts.activeVersion === null
            ? {
                  kind: 'key',
                  key: 'operations.editor.subtitle.neverPublished',
                  params: {},
              }
            : {
                  kind: 'key',
                  key: 'operations.editor.subtitle.activeSince',
                  params: {
                      version: parts.activeVersion,
                      date: parts.activeSince,
                  },
              },
    );

    return clauses;
};

// ── presentational config ───────────────────────────────────────────────────

export const AMOUNT_SOURCES: {
    id: OperationAmountSource;
    labelKey: string;
    hintKey: string;
}[] = [
    {
        id: 'GROSS',
        labelKey: 'operations.editor.amountSource.gross',
        hintKey: 'operations.editor.amountSource.grossHint',
    },
    {
        id: 'NET',
        labelKey: 'operations.editor.amountSource.net',
        hintKey: 'operations.editor.amountSource.netHint',
    },
    {
        id: 'VAT_OF_GROSS',
        labelKey: 'operations.editor.amountSource.vat',
        hintKey: 'operations.editor.amountSource.vatHint',
    },
    {
        id: 'USER_INPUT',
        labelKey: 'operations.editor.amountSource.userInput',
        hintKey: 'operations.editor.amountSource.userInputHint',
    },
];

/**
 * The closed taxonomy on a pair's chip, in the order the picker offers it.
 *
 * `chip` is a pair of Tailwind arbitrary-value utilities reading the tokens
 * `index.css` defines in both themes — so a chip is re-cut for dark mode rather
 * than being a light tint burned into a component.
 *
 * `OTHER` sits last and is what a new pair starts as: it is the honest answer
 * for a pair whose job is not on the list, and it is what stops anyone
 * inventing an eighth role in a text field.
 */
export const PAIR_ROLES: {
    id: OperationPairRole;
    labelKey: string;
    chip: string;
}[] = [
    {
        id: 'COGS',
        labelKey: 'operations.editor.pairRole.COGS',
        chip: 'bg-[var(--op-role-cogs)] text-[var(--op-role-cogs-foreground)]',
    },
    {
        id: 'REVENUE',
        labelKey: 'operations.editor.pairRole.REVENUE',
        chip: 'bg-[var(--op-role-revenue)] text-[var(--op-role-revenue-foreground)]',
    },
    {
        id: 'VAT',
        labelKey: 'operations.editor.pairRole.VAT',
        chip: 'bg-[var(--op-role-vat)] text-[var(--op-role-vat-foreground)]',
    },
    {
        id: 'SETTLEMENT',
        labelKey: 'operations.editor.pairRole.SETTLEMENT',
        chip: 'bg-[var(--op-role-settlement)] text-[var(--op-role-settlement-foreground)]',
    },
    {
        id: 'DISCOUNT',
        labelKey: 'operations.editor.pairRole.DISCOUNT',
        chip: 'bg-[var(--op-role-discount)] text-[var(--op-role-discount-foreground)]',
    },
    {
        id: 'ROUNDING',
        labelKey: 'operations.editor.pairRole.ROUNDING',
        chip: 'bg-[var(--op-role-rounding)] text-[var(--op-role-rounding-foreground)]',
    },
    {
        id: 'OTHER',
        labelKey: 'operations.editor.pairRole.OTHER',
        chip: 'bg-[var(--op-role-other)] text-[var(--op-role-other-foreground)]',
    },
];

export const pairRoleChip = (role: OperationPairRole) =>
    PAIR_ROLES.find((entry) => entry.id === role) ?? PAIR_ROLES[6]!;

export const RECORDABLE_ROLES: { id: UserRole; labelKey: string }[] = [
    { id: 'OrgAdmin', labelKey: 'operations.roles.OrgAdmin' },
    { id: 'BranchAdmin', labelKey: 'operations.roles.BranchAdmin' },
    { id: 'Seller', labelKey: 'operations.roles.Seller' },
];

/**
 * The accounts a fixed line may point at: postable leaves that are not archived.
 *
 * The same rule the server applies (`ACCOUNT_NOT_POSTABLE`,
 * `ACCOUNT_ARCHIVED`), applied here so the author never picks something that
 * will be refused. Ordered by code, which is how an accountant reads a chart.
 */
export const postableAccounts = (
    accounts: readonly ChartAccount[],
): ChartAccount[] =>
    accounts
        .filter((account) => account.isPostable && !account.archived)
        .slice()
        .sort((a, b) => a.code.localeCompare(b.code));

/** `1110 — Cash on hand`, the way the chart's own pickers read. */
export const accountOptionLabel = (account: ChartAccount): string =>
    `${account.code} — ${account.name}`;

// ── server refusals ─────────────────────────────────────────────────────────

/**
 * Where a coded refusal belongs on the screen.
 *
 * Same two rules as `accountErrors`: branch on `error.code` and never on the
 * message text, and put a field error on its field. A pair rule shown in a
 * form-level banner makes the author hunt for which of six pairs it meant.
 */
export type EditorErrorTarget = 'pairs' | 'roles' | 'effectiveFrom' | 'form';

const TARGETS: Record<string, EditorErrorTarget> = {
    NO_PAIRS: 'pairs',
    PAIR_UNBALANCED: 'pairs',
    PAIR_SOURCE_MISMATCH: 'pairs',
    SLOT_INCOMPLETE: 'pairs',
    LINE_ACCOUNT_AMBIGUOUS: 'pairs',
    VAT_WITHOUT_GROSS: 'pairs',
    ACCOUNT_NOT_FOUND: 'pairs',
    ACCOUNT_ARCHIVED: 'pairs',
    ACCOUNT_NOT_POSTABLE: 'pairs',
    ROLES_REQUIRED: 'roles',
    EFFECTIVE_FROM_PAST: 'effectiveFrom',
    EFFECTIVE_FROM_NOT_AFTER_ACTIVE: 'effectiveFrom',
};

export type EditorError = {
    target: EditorErrorTarget;
    /** The server's own message. See below for why it is not translated. */
    message: string;
    code: string | null;
};

/**
 * Turn a failed write into something to render.
 *
 * **The server's prose is shown verbatim**, and that is a considered choice
 * rather than a gap. These messages name the offending pair by number — "Pair 2
 * needs exactly one debit line and one credit line; it has 2 and 0" — and a
 * translated string could not carry that without the server also sending the
 * parameters separately. Anchoring the message to the right part of the form is
 * what `target` is for, and it is the half that matters most.
 *
 * The known consequence: **a Georgian user sees English here.** It is the same
 * trade the periods screen documents for the fiscal-year refusals, and it is
 * tolerable for the same reason — the editor gates on its own rules first, so
 * these are the refusals that got past the form, not the everyday ones.
 */
export const toEditorError = (
    error: ErrorResponse | null,
): EditorError | null => {
    if (!error) {
        return null;
    }
    const code = error.code ?? null;
    return {
        target: (code && TARGETS[code]) || 'form',
        message: error.message,
        code,
    };
};
